import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Segment, BookingData } from '../types/bookingTypes'
import { Travel } from '../types/travelTypes'
import { Passenger } from '../types/passengerTypes'
import { AfterSalesProfile, AfterSalesRule } from '../types/aftersalesTypes'
import { fetchBookingData as fetchBookingDataService } from '../services/journeyService'
import { storeProvisionalBookingInfo } from '../services/storageService'
import { useErrorStore } from '../stores/errorStore'
import { useMyTravelStore } from '../stores/myTravelStore'
import { getActiveSegments } from '../utils/rebooking/dataTransformUtils'

interface Equipment {
  uuid: string
  product_code: string
  product_type: string
  count: number
  price: {
    amount: number
    currency_code: string
  }
  name: string
  tariff_code: string
  passenger_fares: Array<{
    passenger_id: string
    price?: {
      amount: number
      currency_code: string
    }
    tariff_code?: string
  }>
  cancelled?: boolean | string | number
}

interface SelectedEquipment {
  outbound: Equipment[]
  inbound: Equipment[]
}

interface SimpleAfterSalesRule {
  allowed: boolean
  effect: string
  allow_time?: number
}

export const useBookingStore = defineStore('booking', () => {
  const errorStore = useErrorStore()
  const myTravelStore = useMyTravelStore()

  const outboundTravel = ref<Travel | null>(null)
  const inboundTravel = ref<Travel | null>(null)
  const segments = ref<Segment[]>([])
  const passengers = ref<Passenger[]>([])
  const roundTrip = ref<boolean>(false)
  const selectedEquipment = ref<SelectedEquipment>({
    outbound: [],
    inbound: [],
  })

  const bookingEquipment = ref<SelectedEquipment>({
    outbound: [],
    inbound: [],
  })

  const selectedEquipmentTotalPrice = computed(() => {
    const outboundTotal = selectedEquipment.value.outbound.reduce(
      (total, item) => {
        const price = item.price?.amount || 0
        return total + price * item.count
      },
      0
    )
    const inboundTotal = selectedEquipment.value.inbound.reduce(
      (total, item) => {
        const price = item.price?.amount || 0
        return total + price * item.count
      },
      0
    )
    return outboundTotal + inboundTotal
  })

  const getEquipmentCount = (
    direction: 'outbound' | 'inbound',
    productCode: string
  ): number => {
    const bookingCount =
      bookingEquipment.value[direction].find(
        (e) => e.product_code === productCode
      )?.count || 0
    const selectedCount =
      selectedEquipment.value[direction].find(
        (e) => e.product_code === productCode
      )?.count || 0
    return bookingCount + selectedCount
  }

  const getExistingEquipment = (
    direction: 'outbound' | 'inbound',
    productCode: string
  ) => {
    const equipment = bookingEquipment.value[direction]
    return equipment.find((e) => e.product_code === productCode)
  }

  const bookingNumber = ref<string | null>(null)
  const bookingData = ref<BookingData | null>(null)
  const afterSalesProfile = ref<AfterSalesProfile | null>(null)
  const afterSalesProfileRules = ref<SimpleAfterSalesRule[] | null>(null)
  const isRebooking = ref<boolean>(false)
  const bookingToken = ref<string | null>(null) // New field

  const setIsRebooking = (value: boolean) => {
    isRebooking.value = value
  }

  const setBookingNumber = (number: string) => {
    if (typeof number !== 'string' || !number) {
      console.warn('Invalid booking number provided:', number)
      return
    }
    bookingNumber.value = number
  }

  const setOutboundTravel = (
    travel: Travel,
    routeIndex: number,
    bundleIndex: number
  ) => {
    const selectedRoute = travel.routes[routeIndex]
    const selectedBundle = selectedRoute.bundles?.[bundleIndex]

    const bundleCopy = selectedBundle
      ? {
          ...selectedBundle,
          items: selectedBundle.items.map((item) => ({ ...item })),
        }
      : null

    outboundTravel.value = {
      ...travel,
      travel_index: 0,
      routes: [
        {
          ...selectedRoute,
          bundles: bundleCopy ? [bundleCopy] : [],
        },
      ],
    }

    clearSegmentsByDirection('outbound')

    if (selectedRoute.legs?.length) {
      selectedRoute.legs.forEach((leg) => {
        const segmentItems =
          bundleCopy?.items
            .filter((item) => item.leg_ids?.includes(leg.id))
            .flatMap((item) =>
              item.passenger_fares.map((fare) => ({
                passenger_id: fare.passenger_id,
                tariff_code: fare.tariff_code,
                item_ref: fare.ref_id,
              }))
            ) || []

        const validityDate = leg.service_schedule_date.split('T')[0]

        const segment: Segment = {
          client_ref: leg.id,
          departure_station: leg.departure_station,
          arrival_station: leg.arrival_station,
          direction: 'outbound',
          start_validity_date: validityDate,
          service_name: leg.service_name,
          service_identifier: leg.service_identifier,
          origin: leg.departure_station._u_i_c_station_code,
          destination: leg.arrival_station._u_i_c_station_code,
          validity_intervals: [],
          has_new_main_products: false,
          travel_info: [],
          travel_info_extended: [],
          id: leg.id,
          travel_date: validityDate,
          required_products: [],
          validity_service: leg.service_name,
          tariff_group_code: '',
          booking_journey_segments: [
            {
              service_name: leg.service_name,
              departure_date_time:
                leg.departure_timestamp || leg.service_schedule_date,
              departure_station: {
                _u_i_c_station_code: leg.departure_station._u_i_c_station_code,
                name: leg.departure_station.name,
              },
              arrival_station: {
                _u_i_c_station_code: leg.arrival_station._u_i_c_station_code,
                name: leg.arrival_station.name,
              },
            },
          ],
          items: segmentItems,
          additional_tariff_conditions: {
            journey_od: {
              origin: leg.departure_station._u_i_c_station_code,
              destination: leg.arrival_station._u_i_c_station_code,
            },
          },
        }

        addSegment(segment)
      })
    }
  }

  const setInboundTravel = (
    travel: Travel,
    routeIndex: number,
    bundleIndex: number
  ) => {
    const selectedRoute = travel.routes[routeIndex]
    const selectedBundle = selectedRoute.bundles?.[bundleIndex]

    const bundleCopy = selectedBundle
      ? {
          ...selectedBundle,
          items: selectedBundle.items.map((item) => ({ ...item })),
        }
      : null

    inboundTravel.value = {
      ...travel,
      travel_index: 1,
      routes: [
        {
          ...selectedRoute,
          bundles: bundleCopy ? [bundleCopy] : [],
        },
      ],
    }

    clearSegmentsByDirection('inbound')

    if (selectedRoute.legs?.length) {
      selectedRoute.legs.forEach((leg) => {
        const segmentItems =
          bundleCopy?.items
            .filter((item) => item.leg_ids?.includes(leg.id))
            .flatMap((item) =>
              item.passenger_fares.map((fare) => ({
                passenger_id: fare.passenger_id,
                tariff_code: fare.tariff_code,
                item_ref: fare.ref_id,
              }))
            ) || []

        // Format date to YYYY-MM-DD
        const validityDate = leg.service_schedule_date.split('T')[0]

        const segment: Segment = {
          client_ref: leg.id,
          departure_station: leg.departure_station,
          arrival_station: leg.arrival_station,
          direction: 'inbound',
          start_validity_date: validityDate,
          service_name: leg.service_name,
          service_identifier: leg.service_identifier,
          origin: leg.departure_station._u_i_c_station_code,
          destination: leg.arrival_station._u_i_c_station_code,
          validity_intervals: [],
          has_new_main_products: false,
          travel_info: [],
          travel_info_extended: [],
          id: leg.id,
          travel_date: validityDate,
          required_products: [],
          validity_service: leg.service_name,
          tariff_group_code: '',
          booking_journey_segments: [
            {
              service_name: leg.service_name,
              departure_date_time:
                leg.departure_timestamp || leg.service_schedule_date,
              departure_station: {
                _u_i_c_station_code: leg.departure_station._u_i_c_station_code,
                name: leg.departure_station.name,
              },
              arrival_station: {
                _u_i_c_station_code: leg.arrival_station._u_i_c_station_code,
                name: leg.arrival_station.name,
              },
            },
          ],
          items: segmentItems,
          additional_tariff_conditions: {
            journey_od: {
              origin: leg.departure_station._u_i_c_station_code,
              destination: leg.arrival_station._u_i_c_station_code,
            },
          },
        }

        addSegment(segment)
      })
    }
  }

  const addSegment = (segment: Segment) => {
    segments.value.push(segment)
  }

  const clearSegmentsByDirection = (direction: 'outbound' | 'inbound') => {
    segments.value = segments.value.filter(
      (segment) => segment.direction !== direction
    )
  }

  const clearTravels = () => {
    outboundTravel.value = null
    inboundTravel.value = null
    segments.value = []
  }

  const addSegments = (newSegments: Segment[]) => {
    segments.value.push(...newSegments)
  }

  const setPassengers = (passengerList: Passenger[]) => {
    passengers.value = passengerList
  }

  const setRoundTrip = (isRoundTrip: boolean) => {
    roundTrip.value = isRoundTrip
  }

  const addOutboundEquipment = (equipment: Equipment) => {
    const price =
      equipment.passenger_fares?.[0]?.price?.amount ||
      equipment.price?.amount ||
      0
    const equipmentToAdd: Equipment = {
      ...equipment,
      count: 1,
      price: {
        amount: price,
        currency_code: 'NOK',
      },
      name: equipment.name || equipment.product_type,
      tariff_code:
        equipment.passenger_fares?.[0]?.tariff_code ||
        equipment.tariff_code ||
        '',
      passenger_fares:
        equipment.passenger_fares?.map((pf) => ({
          passenger_id: pf.passenger_id,
          price: {
            amount: pf.price?.amount || price,
            currency_code: 'NOK',
          },
          tariff_code: pf.tariff_code || equipment.tariff_code || '',
        })) || [],
    }
    selectedEquipment.value.outbound.push(equipmentToAdd)

    const outboundEquipment = [
      ...bookingEquipment.value.outbound,
      ...selectedEquipment.value.outbound,
    ]
    myTravelStore.updateGroupedEquipment('outbound', outboundEquipment)
  }

  const addInboundEquipment = (equipment: Equipment) => {
    const price =
      equipment.passenger_fares?.[0]?.price?.amount ||
      equipment.price?.amount ||
      0
    const equipmentToAdd: Equipment = {
      ...equipment,
      count: 1,
      price: {
        amount: price,
        currency_code: 'NOK',
      },
      name: equipment.name || equipment.product_type,
      tariff_code:
        equipment.passenger_fares?.[0]?.tariff_code ||
        equipment.tariff_code ||
        '',
      passenger_fares:
        equipment.passenger_fares?.map((pf) => ({
          passenger_id: pf.passenger_id,
          price: {
            amount: pf.price?.amount || price,
            currency_code: 'NOK',
          },
          tariff_code: pf.tariff_code || equipment.tariff_code || '',
        })) || [],
    }
    selectedEquipment.value.inbound.push(equipmentToAdd)

    const inboundEquipment = [
      ...bookingEquipment.value.inbound,
      ...selectedEquipment.value.inbound,
    ]
    myTravelStore.updateGroupedEquipment('inbound', inboundEquipment)
  }

  const removeOutboundEquipment = (equipment: Equipment) => {
    const selectedIndex = selectedEquipment.value.outbound.findIndex(
      (e) => e.product_code === equipment.product_code
    )

    if (selectedIndex !== -1) {
      const item = selectedEquipment.value.outbound[selectedIndex]
      if (item.count > 1) {
        item.count--
      } else {
        selectedEquipment.value.outbound.splice(selectedIndex, 1)
      }

      const validBookingEquipment = bookingEquipment.value.outbound.filter(
        (item) => {
          return !(
            item.cancelled === true ||
            item.cancelled === 'true' ||
            item.cancelled === 1
          )
        }
      )

      const outboundEquipment = [
        ...validBookingEquipment,
        ...selectedEquipment.value.outbound,
      ]

      myTravelStore.updateGroupedEquipment('outbound', outboundEquipment)
    }
  }

  const removeInboundEquipment = (equipment: Equipment) => {
    const selectedIndex = selectedEquipment.value.inbound.findIndex(
      (e) => e.product_code === equipment.product_code
    )

    if (selectedIndex !== -1) {
      const item = selectedEquipment.value.inbound[selectedIndex]
      if (item.count > 1) {
        item.count--
      } else {
        selectedEquipment.value.inbound.splice(selectedIndex, 1)
      }
      const validBookingEquipment = bookingEquipment.value.inbound.filter(
        (item) => {
          return !(
            item.cancelled === true ||
            item.cancelled === 'true' ||
            item.cancelled === 1
          )
        }
      )

      const inboundEquipment = [
        ...validBookingEquipment,
        ...selectedEquipment.value.inbound,
      ]

      myTravelStore.updateGroupedEquipment('inbound', inboundEquipment)
    }
  }

  const setBookingData = (data: BookingData) => {
    if (!data) return

    bookingData.value = data
    bookingNumber.value = data.booking_number || bookingNumber.value
    bookingToken.value = data.bookingToken || bookingToken.value

    if (data.booking_number) {
      const outboundSegments = data.outbound_booking_tariff_segments || []
      const inboundSegments = data.inbound_booking_tariff_segments || []
      const allSegments = [...outboundSegments, ...inboundSegments]

      const isProvisional = allSegments.some((segment) => {
        const requiredProducts = segment.required_products || []
        return requiredProducts.some((product) => product.provisional === true)
      })

      if (isProvisional) {
        storeProvisionalBookingInfo(data.booking_number, true)
      }
    }

    if (data.passengers) {
      passengers.value = data.passengers
    }

    if (data.outbound_booking_tariff_segments?.length) {
      const legs = data.outbound_booking_tariff_segments
        .filter(
          (segment) => segment.departure_station && segment.arrival_station
        )
        .map((segment) => ({
          id: segment.client_ref,
          departure_station: segment.departure_station,
          arrival_station: segment.arrival_station,
          departure_timestamp: segment.start_validity_date || '',
          arrival_timestamp:
            segment.end_validity_date || segment.start_validity_date || '',
          service_name: segment.service_name,
          service_identifier: segment.service_identifier,
          _u_i_c_station_code:
            segment.departure_station?._u_i_c_station_code || '',
          service_schedule_date:
            segment.travel_date || segment.start_validity_date || '',
        }))

      outboundTravel.value = {
        id: `outbound-${data.booking_number || ''}`,
        direction: 'outbound',
        departure_date: legs[0]?.departure_timestamp || '',
        origin_station: legs[0]?.departure_station,
        destination_station: legs[legs.length - 1]?.arrival_station,
        travel_index: 0,
        routes: [
          {
            legs,
            bundles: [],
          },
        ],
        bundles: [],
      }
    } else {
      outboundTravel.value = null
    }

    if (data.inbound_booking_tariff_segments?.length) {
      const legs = data.inbound_booking_tariff_segments
        .filter(
          (segment) => segment.departure_station && segment.arrival_station
        )
        .map((segment) => ({
          id: segment.client_ref,
          departure_station: segment.departure_station,
          arrival_station: segment.arrival_station,
          departure_timestamp: segment.start_validity_date || '',
          arrival_timestamp:
            segment.end_validity_date || segment.start_validity_date || '',
          service_name: segment.service_name,
          service_identifier: segment.service_identifier,
          _u_i_c_station_code:
            segment.departure_station?._u_i_c_station_code || '',
          service_schedule_date:
            segment.travel_date || segment.start_validity_date || '',
        }))

      inboundTravel.value = {
        id: `inbound-${data.booking_number || ''}`,
        direction: 'inbound',
        departure_date: legs[0]?.departure_timestamp || '',
        origin_station: legs[0]?.departure_station,
        destination_station: legs[legs.length - 1]?.arrival_station,
        travel_index: 1,
        routes: [
          {
            legs,
            bundles: [],
          },
        ],
        bundles: [],
      }
    } else {
      inboundTravel.value = null
    }

    const existingOutboundEquipment =
      myTravelStore.outboundGroupedEquipment || []
    const existingInboundEquipment = myTravelStore.inboundGroupedEquipment || []

    selectedEquipment.value = { outbound: [], inbound: [] }
    bookingEquipment.value = { outbound: [], inbound: [] }

    if (data.outbound_booking_tariff_segments) {
      const productsByUuid = new Map<string, any>()
      // Only process active (non-cancelled) segments for equipment products
      console.log('[DEBUG] BookingStore calling getActiveSegments for outbound segments')
      const activeOutboundSegments = getActiveSegments(data.outbound_booking_tariff_segments)
      console.log('[DEBUG] BookingStore received active outbound segments:', activeOutboundSegments)
      activeOutboundSegments.forEach((segment) => {
        segment.additional_products?.forEach((product) => {
          let isCancelled = false
          if (typeof product.cancelled === 'boolean') {
            isCancelled = product.cancelled === true
          } else if (typeof product.cancelled === 'string') {
            isCancelled =
              product.cancelled === 'true' || product.cancelled === '1'
          } else if (typeof product.cancelled === 'number') {
            isCancelled = product.cancelled === 1
          }

          if (!isCancelled) {
            productsByUuid.set(product.uuid, product)
          }
        })
      })

      const outboundEquipmentItems: Equipment[] = []
      productsByUuid.forEach((product) => {
        if (product.product_type === 'EQUIPMENT') {
          outboundEquipmentItems.push({
            uuid: product.uuid,
            product_code: product.product_code,
            product_type: product.product_type,
            count: product.count || 1,
            price: product.price,
            name: product.name,
            tariff_code: product.tariff_code,
            passenger_fares: product.passenger_fares || [],
          })
        }
      })

      bookingEquipment.value.outbound = outboundEquipmentItems
    }

    if (data.inbound_booking_tariff_segments) {
      const productsByUuid = new Map<string, any>()
      // Only process active (non-cancelled) segments for equipment products
      const activeInboundSegments = getActiveSegments(data.inbound_booking_tariff_segments)
      activeInboundSegments.forEach((segment) => {
        segment.additional_products?.forEach((product) => {
          let isCancelled = false
          if (typeof product.cancelled === 'boolean') {
            isCancelled = product.cancelled === true
          } else if (typeof product.cancelled === 'string') {
            isCancelled =
              product.cancelled === 'true' || product.cancelled === '1'
          } else if (typeof product.cancelled === 'number') {
            isCancelled = product.cancelled === 1
          }

          if (!isCancelled) {
            productsByUuid.set(product.uuid, product)
          }
        })
      })

      const inboundEquipmentItems: Equipment[] = []
      productsByUuid.forEach((product) => {
        if (product.product_type === 'EQUIPMENT') {
          inboundEquipmentItems.push({
            uuid: product.uuid,
            product_code: product.product_code,
            product_type: product.product_type,
            count: product.count || 1,
            price: product.price,
            name: product.name,
            tariff_code: product.tariff_code,
            passenger_fares: product.passenger_fares || [],
          })
        }
      })

      bookingEquipment.value.inbound = inboundEquipmentItems
    }

    // Only load equipment into myTravelStore if we're NOT in rebooking mode
    // This prevents original booking equipment from being included in the rebooking price calculation
    if (!myTravelStore.isRebooking) {
      if (
        existingOutboundEquipment.length === 0 &&
        bookingEquipment.value.outbound.length > 0
      ) {
        const apiOutboundEquipment = bookingEquipment.value.outbound
        if (apiOutboundEquipment.length > 0) {
          myTravelStore.updateGroupedEquipment(
            'outbound',
            apiOutboundEquipment,
            true
          )
        }
      }

      // Same logic for inbound equipment
      if (
        existingInboundEquipment.length === 0 &&
        bookingEquipment.value.inbound.length > 0
      ) {
        const apiInboundEquipment = bookingEquipment.value.inbound
        if (apiInboundEquipment.length > 0) {
          myTravelStore.updateGroupedEquipment(
            'inbound',
            apiInboundEquipment,
            true
          )
        }
      }
    } 
  }

  const setBooking = (data: any) => {
    bookingData.value = data
  }

  const extractAfterSalesRulesFromBooking = (booking: BookingData) => {
    const rules: any[] = []

    if (booking.outbound_booking_tariff_segments) {
      booking.outbound_booking_tariff_segments.forEach((segment) => {
        if (segment.cancelled) return

        segment.required_products?.forEach((product) => {
          if (product.cancelled) return

          if (product.after_sales_rules) {
            rules.push(...product.after_sales_rules)
          }
        })
      })
    }

    if (booking.inbound_booking_tariff_segments) {
      booking.inbound_booking_tariff_segments.forEach((segment) => {
        if (segment.cancelled) return

        segment.required_products?.forEach((product) => {
          if (product.cancelled) return

          if (product.after_sales_rules) {
            rules.push(...product.after_sales_rules)
          }
        })
      })
    }

    return rules
  }

  // Helper function to calculate allow_time based on valid_until date
  const calculateAllowTime = (validUntilStr?: string) => {
    if (!validUntilStr) return 0

    const validUntil = new Date(validUntilStr)
    const now = new Date()

    // Calculate days difference
    const diffTime = validUntil.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays > 0 ? diffDays : 0
  }

  const fetchAfterSalesProfile = async (bookingNum: string) => {
    try {
      // If booking data is not already loaded, load it
      if (
        !bookingData.value ||
        bookingData.value.booking_number !== bookingNum
      ) {
        await fetchBookingData(bookingNum)
      }

      if (!bookingData.value) {
        throw new Error('Booking data not available')
      }

      // Extract after-sales rules from booking segments
      const extractedRules = extractAfterSalesRulesFromBooking(
        bookingData.value
      )

      // Set the after-sales profile data
      afterSalesProfile.value = {
        id: bookingData.value.booking_id,
        rebook_ruleset: {
          rules: extractedRules.filter((rule) => rule.type === 'REBOOK'),
        },
        cancel_ruleset: {
          rules: extractedRules.filter((rule) => rule.type === 'CANCEL'),
        },
      }

      // Format the rules to match the expected structure
      afterSalesProfileRules.value = extractedRules
        .filter((rule) => rule.type === 'REBOOK')
        .map(
          (rule, index): AfterSalesRule => ({
            sequence: index,
            allowed: rule.active,
            effect: rule.active ? 'ALLOW' : undefined,
            allow_time: calculateAllowTime(rule.valid_until),
          })
        )
    } catch (error) {
      console.error('Error processing after sales rules:', error)
      errorStore.setError('Error processing after sales rules')
      return null
    }
  }

  const fetchBookingData = async (bookingNum: string) => {
    try {
      const response = await fetchBookingDataService(bookingNum)
      if (response?.data?.booking) {
        setBookingData(response.data.booking)
      } else {
        console.error('Invalid booking data structure:', response)
        throw new Error('Invalid booking data structure')
      }
    } catch (error) {
      console.error('Error fetching booking data:', error)
      throw error
    }
  }

  const canRebookJourney = (direction: 'outbound' | 'inbound'): boolean => {
    if (!bookingData.value) return false

    const segments =
      direction === 'outbound'
        ? bookingData.value.outbound_booking_tariff_segments
        : bookingData.value.inbound_booking_tariff_segments

    if (!segments?.length) return false

    const segmentsWithProducts = segments.filter(
      (segment) => !segment.cancelled && segment.required_products?.length > 0
    )

    if (segmentsWithProducts.length === 0) return false

    return segmentsWithProducts.some((segment) => {
      return segment.required_products.some((product) => {
        if (product.cancelled) return false

        return (
          product.can_be_rebooked === true &&
          product.after_sales_rules?.some(
            (rule) =>
              rule.active &&
              rule.type === 'REBOOK' &&
              new Date() <= new Date(rule.valid_until)
          )
        )
      })
    })
  }

  const getRebookingRestrictions = () => {
    if (!bookingData.value) return null

    let validUntilDate: Date | null = null

    if (bookingData.value.outbound_booking_tariff_segments) {
      for (const segment of bookingData.value
        .outbound_booking_tariff_segments) {
        if (segment.cancelled) continue

        for (const product of segment.required_products || []) {
          if (product.cancelled) continue

          for (const rule of product.after_sales_rules || []) {
            if (rule.active && rule.type === 'REBOOK' && rule.valid_until) {
              const ruleDate = new Date(rule.valid_until)
              if (!validUntilDate || ruleDate > validUntilDate) {
                validUntilDate = ruleDate
              }
            }
          }
        }
      }
    }

    if (bookingData.value.inbound_booking_tariff_segments) {
      for (const segment of bookingData.value.inbound_booking_tariff_segments) {
        if (segment.cancelled) continue

        for (const product of segment.required_products || []) {
          if (product.cancelled) continue

          for (const rule of product.after_sales_rules || []) {
            if (rule.active && rule.type === 'REBOOK' && rule.valid_until) {
              const ruleDate = new Date(rule.valid_until)
              if (!validUntilDate || ruleDate > validUntilDate) {
                validUntilDate = ruleDate
              }
            }
          }
        }
      }
    }

    if (!validUntilDate) return null

    const now = new Date()
    return {
      validUntil: validUntilDate,
      isWithinValidityPeriod: now <= validUntilDate,
    }
  }

  const transformSegmentForApi = (segment: Segment) => {
    return {
      service_identifier: segment.service_identifier,
      service_name: segment.service_name,
      origin: segment.origin,
      destination: segment.destination,
      direction: segment.direction,
      start_validity_date: segment.start_validity_date,
      items: segment.items.map((item) => ({
        passenger_id: item.passenger_id,
        tariff_code: item.tariff_code,
        item_ref: item.item_ref || item.client_ref || '',
      })),
    }
  }

  const getBookingRequestData = () => {
    return {
      currency: 'NOK',
      passengers: passengers.value.map((p) => ({
        id: p.id,
        type: p.type,
        discount_cards: p.discount_cards || [],
      })),
      segments: segments.value.map(transformSegmentForApi),
    }
  }

  const refreshOutboundEquipment = () => {
    const validEquipment = bookingEquipment.value.outbound.filter((item) => {
      let isCancelled = false
      if (typeof item.cancelled === 'boolean') {
        isCancelled = item.cancelled === true
      } else if (typeof item.cancelled === 'string') {
        isCancelled = item.cancelled === 'true' || item.cancelled === '1'
      } else if (typeof item.cancelled === 'number') {
        isCancelled = item.cancelled === 1
      }
      return !isCancelled
    })

    bookingEquipment.value.outbound = validEquipment
    myTravelStore.updateGroupedEquipment('outbound', validEquipment)
  }

  const refreshInboundEquipment = () => {
    const validEquipment = bookingEquipment.value.inbound.filter((item) => {
      // Check all possible representations of cancelled status in a type-safe way
      let isCancelled = false
      if (typeof item.cancelled === 'boolean') {
        isCancelled = item.cancelled === true
      } else if (typeof item.cancelled === 'string') {
        isCancelled = item.cancelled === 'true' || item.cancelled === '1'
      } else if (typeof item.cancelled === 'number') {
        isCancelled = item.cancelled === 1
      }
      return !isCancelled
    })
    bookingEquipment.value.inbound = validEquipment
    myTravelStore.updateGroupedEquipment('inbound', validEquipment)
  }

  const $reset = () => {
    const existingOutboundEquipment = selectedEquipment.value.outbound
    const existingInboundEquipment = selectedEquipment.value.inbound

    outboundTravel.value = null
    inboundTravel.value = null
    segments.value = []
    passengers.value = []
    roundTrip.value = false
    selectedEquipment.value = {
      outbound: [],
      inbound: [],
    }
    bookingEquipment.value = {
      outbound: [],
      inbound: [],
    }
    bookingNumber.value = null
    bookingData.value = null
    afterSalesProfile.value = null
    afterSalesProfileRules.value = null
    isRebooking.value = false

    // Restore equipment state to myTravelStore
    myTravelStore.updateGroupedEquipment('outbound', existingOutboundEquipment)
    myTravelStore.updateGroupedEquipment('inbound', existingInboundEquipment)
  }

  const canCancelBooking = (booking: BookingData | null): boolean => {
    if (!booking) return false
    return !booking.confirmed_timestamp
  }

  return {
    outboundTravel,
    inboundTravel,
    segments,
    passengers,
    roundTrip,
    bookingNumber,
    bookingData,
    afterSalesProfile,
    afterSalesProfileRules,
    selectedEquipment,
    bookingEquipment,
    selectedEquipmentTotalPrice,
    isRebooking,
    bookingToken,
    setBookingNumber,
    setOutboundTravel,
    setInboundTravel,
    addSegment,
    addSegments,
    clearSegmentsByDirection,
    clearTravels,
    setPassengers,
    setRoundTrip,
    addOutboundEquipment,
    addInboundEquipment,
    removeOutboundEquipment,
    removeInboundEquipment,
    setBookingData,
    setBooking,
    fetchAfterSalesProfile,
    fetchBookingData,
    canRebookJourney,
    getRebookingRestrictions,
    transformSegmentForApi,
    getBookingRequestData,
    getEquipmentCount,
    getExistingEquipment,
    setIsRebooking,
    refreshOutboundEquipment,
    refreshInboundEquipment,
    $reset,
    canCancelBooking,
  }
})
